import { A4_LANDSCAPE } from '@/constants/pageFormat';

/** Paginakleur achter de vellen; gelijk aan de achtergrond eromheen. */
const SHEET_GAP_COLOR = 'var(--quote-sheet-gap-color, #f5f6f8)';

/**
 * Opmaak van het document op A4-vellen (`paginateDocument`), gelijk in de
 * editor, de klantweergave en de PDF. `scope` is het element met
 * `data-sheet-mode` waarin de editor en de overlay van `SheetGaps` staan.
 */
export function sheetStyles(scope: string): string {
  return `
    /*
     * De paginering haalt verschuivingen weg, meet en zet ze terug. Scroll
     * anchoring zou de lezer daarbij verplaatsen zonder het terug te draaien.
     */
    ${scope}[data-sheet-mode='web'] {
      overflow-anchor: none;
    }

    /* Elk blok beslaat een geheel aantal vellen (min-height via de paginering). */
    ${scope} .ProseMirror > .react-renderer {
      position: relative;
      width: ${A4_LANDSCAPE.width}px !important;
    }

    ${scope}[data-sheet-mode='web'] .ProseMirror > .react-renderer {
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08), 0 0 0 1px rgba(15, 23, 42, 0.06);
    }

    ${scope}[data-sheet-mode='web'] .ProseMirror > .react-renderer:not(:last-of-type) {
      margin-bottom: ${A4_LANDSCAPE.pageGap}px;
    }

    ${scope} .shared-sheet-gap,
    ${scope} .sheet-insert-gap {
      position: absolute;
      left: -4px;
      right: -4px;
      background: ${SHEET_GAP_COLOR};
    }

    ${scope} .shared-sheet-gap {
      box-shadow: inset 0 1px 0 rgba(15, 23, 42, 0.06), inset 0 -1px 0 rgba(15, 23, 42, 0.06);
    }

    /*
     * Full-bleed pagina's krijgen hun afbeelding uitsluitend van het fysieke
     * vel (zie useSheetLayout). De contentlaag blijft transparant, zodat de
     * afbeelding niet nogmaals op de 5 mm kortere bloklaag wordt geschaald.
     */
    ${scope} .block-node-view[data-full-bleed-background='true'] {
      background-color: transparent !important;
      background-image: none !important;
    }

    ${scope} .block-node-view[data-full-bleed-background='true']::before,
    ${scope} .block-node-view[data-full-bleed-background='true'] [data-full-bleed-artwork] {
      display: none !important;
    }

    ${scope} .block-node-view[data-full-bleed-background='true'] [data-full-bleed-surface] {
      background-color: transparent !important;
    }

    /* Verschuivingen uit de paginering: lege blokhoogte (zie sheetPagination). */
    /* De lege hoogte moet Chromium over twee vellen kunnen verdelen. */
    .tiptap-editor [data-sheet-push] {
      break-inside: auto !important;
      page-break-inside: auto !important;
    }

    .tiptap-editor [data-sheet-push]::before {
      content: '';
      display: block;
      height: var(--sheet-push);
      pointer-events: none;
    }

    .tiptap-editor .sheet-push-spacer {
      display: block;
      pointer-events: none;
    }

    .tiptap-editor [data-sheet-shrink] .resizable-image-wrapper > div > div {
      max-width: min(100%, var(--sheet-image-max-width)) !important;
    }

    /*
     * Meten zonder de verschuivingen van de vorige ronde (zie paginateDocument).
     * Niet --sheet-content-min-height zelf terugzetten: een overgeërfde variabele
     * laat de browser de stijl van het hele document opnieuw berekenen.
     */
    .tiptap-editor[data-sheet-measuring] > .react-renderer {
      min-height: 0 !important;
    }

    .tiptap-editor[data-sheet-measuring] > .react-renderer > .section-node-view > div {
      min-height: ${A4_LANDSCAPE.contentHeight}px !important;
    }

    .tiptap-editor[data-sheet-measuring] [data-sheet-push]::before,
    .tiptap-editor[data-sheet-measuring] .sheet-push-spacer {
      display: none !important;
    }

    .tiptap-editor[data-sheet-measuring] [data-sheet-shrink] {
      --sheet-image-max-width: 100000px !important;
    }

    /*
     * Tabellen zijn grids: table en tbody vallen weg, elke rij is een grid
     * (kolommen uit de tableRow-nodeview van TableKit).
     */
    .tiptap-editor table {
      border-collapse: collapse;
      width: 100%;
      display: contents;
    }

    .tiptap-editor table tbody {
      width: 100%;
      display: contents;
    }

    .tiptap-editor table tbody tr,
    .tiptap-editor table tr {
      width: 100%;
      position: relative;
      box-sizing: border-box;
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
    }

    .tiptap-editor table tbody tr td,
    .tiptap-editor table tbody tr th,
    .tiptap-editor table tr td,
    .tiptap-editor table tr th {
      box-sizing: border-box;
      position: relative;
    }
  `;
}
